import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import env from 'Env';
import ProductCover from 'Components/Utility/ProductCover';
import Drm from 'Components/Utility/Drm';
import CountdownTimer from 'Components/Utility/CountdownTimer';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import StarRating from 'Components/ProductReviews/UserReviews/StarRating';

import 'Layout/NavBar/MegaMenu/StarDealTab.scss';

export default function StarDealTab({
  starDeal,
  setMegaMenuTab,
  tab,
  section,
}) {
  if (!starDeal) return false;

  const onClick = () => {
    setMegaMenuTab('');
  };

  const { keysAvailablePercentage } = starDeal;

  const styling = {
    keys: {
      width: `${keysAvailablePercentage}%`,
    },
  };

  let validDrm = '';
  let filteredDrm = [];
  if (starDeal && starDeal.drm) filteredDrm = starDeal.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const scoreThreshold = env.percentRecommendedThreshold;

  const showMetacritic = starDeal.metacritic_score && starDeal.metacritic_score >= scoreThreshold;
  const showOpencritic = starDeal.opencritic_score && starDeal.opencritic_score >= scoreThreshold;

  const operatingSystems = starDeal.operating_systems || [];

  return (
    <div className={classNames(`ProductsMegaMenu__tab ProductsMegaMenu__tab--${section}`, { 'ProductsMegaMenu__tab--visible': tab === section })}>
      <div className="ProductsMegaMenu__tabTitle ProductsMegaMenu__tabTitle--stardeal">
        <div className="ProductsMegaMenu__tabTitle__starDealImg">
          <img loading="lazy" src="https://cdn.fanatical.com/production/brand/star-deal-icon.svg" alt="Star Deal" />
        </div>
        <FormattedMessage id="stardealtab.title" defaultMessage="Star Deal" />
      </div>

      <div className="ProductsMegaMenu__tabText">
        <FormattedMessage
          id="stardealtab.subtext"
          defaultMessage="Available for a limited time, grab a Star Deal and save big."
        />
      </div>

      <Link
        to={`/${starDeal.type}/${starDeal.slug}`} className="StarDealTab"
        onClick={onClick}
      >
        <div className="StarDealTab__coverContainer">
          <ProductCover
            cover={starDeal.cover}
            alt={`Product cover for ${starDeal.name}`}
            displayType={starDeal.display_type}
            sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 992px) 472px, (min-width: 768px) 352px, (min-width: 576px) 524px, 50vw"
          />
        </div>
        <div className="StarDealTab__detailsContainer">
          <div className="StarDealTab__detailsTop">
            <div className="StarDealTab__detailsTop__name">
              {starDeal.name}
            </div>
            <div className="StarDealTab__detailsTop__reviews">
              {/* We only want to show the score if its atleast 4 stars */}
              {(!!starDeal.rating_score && starDeal.rating_score >= 4) &&
                <div className="StarDealTab__detailsTop__stars">
                  <StarRating lightTheme rating={starDeal.rating_score} />
                  ({starDeal.total_ratings})
                </div>
              }

              {showMetacritic &&
                <div className="StarDealTab__detailsTop__critics">
                  <div className="StarDealTab__detailsTop__criticsScore">
                    {starDeal.metacritic_score}
                  </div>
                  <div className="StarDealTab__detailsTop__criticsLogo StarDealTab__detailsTop__criticsLogo--metacritic">
                    <img src="https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-full-black.svg" alt="" />
                  </div>
                </div>
              }

              {!showMetacritic && showOpencritic &&
                <div className="StarDealTab__detailsTop__critics">
                  <div className="StarDealTab__detailsTop__criticsScore">
                    {starDeal.opencritic_score}
                  </div>
                  <div className="StarDealTab__detailsTop__criticsLogo StarDealTab__detailsTop__criticsLogo--opencritic">
                    <img src="https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-full-black.svg" alt="" />
                  </div>
                </div>
              }
            </div>

            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated star-deal"
                role="progressbar"
                style={!starDeal.sold_out ? styling.keys : { width: '0%' }}
                aria-valuenow={!starDeal.sold_out ? keysAvailablePercentage : 0}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-label="Keys remaining"
                title="Keys remaining"
              />
              {keysAvailablePercentage >= 85 &&
                <div className="progress-timer progress-timer-overlay">
                  {keysAvailablePercentage}% &nbsp;<FormattedMessage id="stardealtab.progress.remaininghigh" defaultMessage="Remaining" tagName="span" />
                </div>
              }
              {keysAvailablePercentage < 85 && keysAvailablePercentage >= 20 &&
                <div className="progress-timer progress-timer-overlay">
                  {keysAvailablePercentage}% &nbsp;<FormattedMessage id="stardealtab.progress.remaining" defaultMessage="keys left" tagName="span" />
                </div>
              }
              {keysAvailablePercentage < 20 &&
                <div className="progress-timer progress-timer-overlay">
                  <FormattedMessage id="stardealtab.progress.remaininglow" defaultMessage="Last Few Keys Remaining" tagName="span" />
                </div>
              }
            </div>
            {!starDeal.sold_out &&
              <div className="StarDealTab__detailsTop__timer">
                <CountdownTimer
                  noLabel
                  featuredDeal
                  value={starDeal.available_valid_until * 1000}
                />
              </div>
            }
          </div>
          <div className="StarDealTab__detailsBottom">
            <div className="StarDealTab__detailsBottom__iconsContainer">
              {validDrm &&
                <Drm drmType={validDrm} colorOverride="dark" />
              }
              {operatingSystems.includes('windows') &&
                <span>
                  <FontAwesomeIcon icon={fabFaWindows} size="lg" className="mr-2" />
                </span>
              }
              {operatingSystems.includes('mac') &&
                <span>
                  <FontAwesomeIcon icon={fabFaApple} size="lg" className="mr-2" />
                </span>
              }
              {operatingSystems.includes('linux') &&
                <span>
                  <FontAwesomeIcon icon={fabFaLinux} size="lg" />
                </span>
              }
            </div>
            <div className="StarDealTab__detailsBottom__priceContainer">
              <PriceContainer
                price={starDeal.price}
                fullPrice={starDeal.fullPrice}
                discountPercent={starDeal.discount_percent}
                hideDiscount={starDeal.hide_discount}
                slug={starDeal.slug}
                payWhatYouWant={starDeal.pay_what_you_want}
              />
            </div>
          </div>
        </div>
      </Link>

      <Link to={`/${starDeal.type}/${starDeal.slug}`} className="ProductsMegaMenu__tabButton" onClick={() => setMegaMenuTab('')}>
        <FormattedMessage id="stardealtab.button.view" defaultMessage="View Star Deal" />
      </Link>
    </div>
  );
}

StarDealTab.propTypes = {
  starDeal: PropTypes.shape({}),
  setMegaMenuTab: PropTypes.func,
  section: PropTypes.string,
  tab: PropTypes.string,
};

StarDealTab.defaultProps = {
  starDeal: {},
  setMegaMenuTab: () => { },
  section: '',
  tab: '',
};
